<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="patrons-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('patron.title') }}</h1>
          <p class="page-subtitle">{{ $t('patron.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="showAddModal = true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('patron.addPatron') }}</span>
        </IntercomButton>
      </div>

      <!-- Search -->
      <div class="search-bar">
        <IntercomInput
          v-model="searchQuery"
          :placeholder="$t('search.patronsPlaceholder')"
          type="search"
          @input="handleSearch"
        />
      </div>

      <!-- Patrons Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="items"
          :loading="loading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-patron_type="{ value }">
            <IntercomBadge v-if="value" :variant="getTypeVariant(value)">
              {{ $t(`patron.types.${value}`) }}
            </IntercomBadge>
            <span v-else class="text-muted">-</span>
          </template>
          <template #cell-total_donated="{ value }">
            {{ formatCompactCurrency(value || 0) }}
          </template>
        </IntercomTable>
        <p v-if="!loading && items.length === 0" class="empty-text">
          {{ $t('patron.noPatrons') }}
        </p>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset.value = v; fetch() }"
      />

      <!-- Add/Edit Modal -->
      <IntercomModal v-model:show="showAddModal" :title="editingPatron ? $t('patron.editPatron') : $t('patron.addPatron')">
        <PatronForm
          :patron="editingPatron"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomInput,
  IntercomModal,
  IntercomPagination,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import PatronForm from '../components/PatronForm.vue'
import { useNavigation } from '../composables/useNavigation.js'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listPatrons, createPatron, updatePatron } = useSeva()
const { navSections } = useNavigation()

// State
const isSaving = ref(false)
const searchQuery = ref('')
const showAddModal = ref(false)
const editingPatron = ref(null)

// Paginated list
const {
  items,
  total,
  loading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList(
  (params) => listPatrons({
    ...params,
    ...(searchQuery.value ? { search: searchQuery.value } : {}),
  }),
  { immediate: false }
)

// Table columns
const columns = [
  { key: 'patron_number', title: t('patron.number'), width: '120px' },
  { key: 'name', title: t('patron.name'), width: '200px' },
  { key: 'phone', title: t('patron.phone'), width: '120px' },
  { key: 'patron_type', title: t('patron.type'), width: '120px' },
  { key: 'total_donated', title: t('patron.totalDonated'), width: '120px', align: 'right' },
]

const getTypeVariant = (type) => {
  switch (type) {
    case 'individual': return 'info'
    case 'corporate': return 'success'
    case 'foundation': return 'warning'
    default: return 'default'
  }
}

const handleSearch = () => {
  resetAndFetch()
}

const handleRowClick = (patron) => {
  router.push(`/patrons/${patron.id}`)
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    // Strip +91 prefix — backend expects 10 raw digits
    const data = { ...formData }
    if (data.phone && data.phone.startsWith('+91')) data.phone = data.phone.slice(3)
    if (!data.phone) data.phone = null

    if (editingPatron.value) {
      await updatePatron(editingPatron.value.id, data)
      toast.success(t('success.patronUpdated'))
    } else {
      await createPatron(data)
      toast.success(t('success.patronCreated'))
    }
    closeModal()
    fetch()
  } catch (error) {
    console.error('Failed to save patron:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const closeModal = () => {
  showAddModal.value = false
  editingPatron.value = null
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetch()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.patrons-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.search-bar {
  margin-bottom: var(--space-6);
  max-width: 400px;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .patrons-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }
}
</style>
